import { SkillDetail, SkillListFilter, SkillMetadata } from "@models";
import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useParams, useSearchParams } from "react-router-dom";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { SkillsListPanel } from "src/components/Skills/LeftPanel/SkillsListPanel";
import { SkillDetailPanel } from "src/components/Skills/RightPanel/SkillDetailPanel";
import {
  canCreateInternalSkill,
  handleSkillMarkdownUpload,
  isAdminUser,
} from "src/components/Skills/skill-utils";
import {
  SkillFormValues,
  toSkillWritePayload,
} from "src/components/Skills/SkillFormModal";
import { useOpenSkillDraftAgentPanel } from "src/components/Skills/useOpenSkillDraftAgentPanel";
import { useSkillInlineFormState } from "src/components/Skills/useSkillInlineFormState";
import {
  useCreateSkillMutation,
  useDeleteSkillMutation,
  useDuplicateSkillMutation,
  useGetSkill,
  useGetSkills,
  useGetUser,
  usePatchSkillMutation,
  useSetSkillEnabledMutation,
} from "src/hooks";

const serializeSkillToMarkdown = (skill: SkillDetail) => {
  const quoteSkillFrontmatterValue = (value: string) =>
    JSON.stringify(value.replace(/\s*\r?\n\s*/g, " ").trim());

  return [
    "---",
    `name: ${quoteSkillFrontmatterValue(skill.name)}`,
    `description: ${quoteSkillFrontmatterValue(skill.descriptionMd)}`,
    `visibility: ${quoteSkillFrontmatterValue(skill.visibility)}`,
    "---",
    "",
    skill.contentMd,
  ].join("\n");
};

const downloadSkill = (skill: SkillDetail) => {
  const url = URL.createObjectURL(
    new Blob([serializeSkillToMarkdown(skill)], {
      type: "text/markdown;charset=utf-8",
    })
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${skill.name}.md`;
  anchor.click();
  URL.revokeObjectURL(url);
};

// react-doctor-disable-next-line react-doctor/no-giant-component, react-doctor/prefer-useReducer -- This page coordinates the full management workflow; extraction is intentionally deferred to avoid broad behavior churn on DEV-2479.
export function Skills() {
  const { orgId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<SkillListFilter>("all");
  const [search, setSearch] = useState("");
  const [selectedSkillId, setSelectedSkillId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<SkillMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const {
    formMode,
    formInitialValues,
    formValues,
    setFormValues,
    isDiscardConfirmOpen,
    beginForm,
    closeForm,
    runAfterDiscardCheck,
    confirmDiscardChanges,
    abortDiscardChanges,
  } = useSkillInlineFormState();

  const { data: user } = useGetUser();
  const { data: skills = [], isLoading: skillsLoading } = useGetSkills({
    orgId,
    filter,
    search,
  });
  const { data: selectedSkill, isLoading: detailLoading } = useGetSkill({
    orgId,
    skillId: selectedSkillId,
    enabled: !!selectedSkillId,
  });

  const createSkill = useCreateSkillMutation();
  const patchSkill = usePatchSkillMutation();
  const setSkillEnabled = useSetSkillEnabledMutation();
  const duplicateSkill = useDuplicateSkillMutation();
  const deleteSkill = useDeleteSkillMutation();
  const openSkillDraftAgentPanel = useOpenSkillDraftAgentPanel();
  const requestedSkillId = searchParams.get("skillId") ?? "";

  useEffect(() => {
    const requestedSkill = requestedSkillId
      ? skills.find((skill) => skill.id === requestedSkillId)
      : undefined;
    const nextSelectedSkillId =
      requestedSkill?.id ||
      (skills.some((skill) => skill.id === selectedSkillId)
        ? selectedSkillId
        : skills[0]?.id) ||
      "";

    if (nextSelectedSkillId !== selectedSkillId) {
      setSelectedSkillId(nextSelectedSkillId);
    }

    if (requestedSkill) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("skillId");
          return next;
        },
        { replace: true }
      );
    }
  }, [requestedSkillId, selectedSkillId, setSearchParams, skills]);

  const openCreateForm = () => {
    runAfterDiscardCheck(() => {
      setError(null);
      beginForm("create");
    });
  };

  const openUploadPicker = () => {
    runAfterDiscardCheck(() => uploadInputRef.current?.click());
  };

  const openEditForm = () => {
    if (!selectedSkill) return;
    runAfterDiscardCheck(() => {
      setError(null);
      beginForm("edit", selectedSkill);
    });
  };

  const openDuplicateForm = () => {
    if (!selectedSkill) return;
    runAfterDiscardCheck(() => {
      setError(null);
      beginForm("duplicate", {
        ...selectedSkill,
        name: `${selectedSkill.name}-copy`,
        visibility:
          selectedSkill.visibility === "GLOBAL" ||
          selectedSkill.visibility === "FORMLY_INTERNAL"
            ? "PRIVATE"
            : selectedSkill.visibility,
      });
    });
  };

  const requestCloseForm = () => {
    runAfterDiscardCheck(closeForm);
  };

  const requestOpenSkillSettings = (openSettings: () => void) => {
    runAfterDiscardCheck(() => {
      closeForm();
      openSettings();
    });
  };

  const requestDeleteSelectedSkill = () => {
    if (!selectedSkill) return;

    runAfterDiscardCheck(() => {
      closeForm();
      setDeleteTarget(selectedSkill);
    });
  };

  const requestSelectSkill = (skillId: string) => {
    runAfterDiscardCheck(() => {
      closeForm();
      setSelectedSkillId(skillId);
    });
  };

  const draftFormWithAgent = (values: SkillFormValues) => {
    if (!formMode) return;

    const operation =
      formMode === "edit"
        ? "edit"
        : formMode === "duplicate"
          ? "duplicate"
          : "create";
    const sourceSkillId =
      operation === "edit"
        ? values.id
        : operation === "duplicate"
          ? selectedSkill?.id
          : undefined;

    openSkillDraftAgentPanel({
      operation,
      sourceSkillId,
    });
  };

  const saveSkill = async (values: SkillFormValues) => {
    if (!formMode) return;
    setError(null);
    const data = toSkillWritePayload(values);

    try {
      if (formMode === "edit" && values.id) {
        setSelectedSkillId(
          (
            await patchSkill.mutateAsync({
              orgId,
              skillId: values.id,
              data,
            })
          ).id
        );
      } else if (formMode === "duplicate" && selectedSkill) {
        setSelectedSkillId(
          (
            await duplicateSkill.mutateAsync({
              orgId,
              skillId: selectedSkill.id,
              data,
            })
          ).id
        );
      } else {
        setSelectedSkillId((await createSkill.mutateAsync({ orgId, data })).id);
      }
      closeForm();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not save the skill."
      );
    }
  };

  const handleUpload = async (file?: File) => {
    await handleSkillMarkdownUpload({
      file,
      input: uploadInputRef.current,
      onDraft: (draft) => beginForm("upload", draft),
      onError: setError,
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSkill.mutateAsync({ orgId, skillId: deleteTarget.id });
      setDeleteTarget(null);
      setSelectedSkillId("");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not delete the skill."
      );
    }
  };

  const toggleSelectedSkillEnabled = async (enabled: boolean) => {
    if (!selectedSkill) return;
    setError(null);
    try {
      await setSkillEnabled.mutateAsync({
        orgId,
        skillId: selectedSkill.id,
        enabled,
      });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not update skill enabled state in skill lists."
      );
    }
  };

  const toggleSelectedSkillForceEnabled = async (forceEnabled: boolean) => {
    if (!selectedSkill) return;
    setError(null);
    try {
      await patchSkill.mutateAsync({
        orgId,
        skillId: selectedSkill.id,
        data: { forceEnabled },
      });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not update force-enabled state."
      );
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        bgcolor: "white",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      data-testid="skills-page"
    >
      <PageHeader
        pageTitleComponent={<PageBreadcrumb items={[{ label: "Skills" }]} />}
        rightPanelToggleRequiresDevice={false}
      />
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
        <PanelGroup
          direction="horizontal"
          className="h-full min-h-0 w-full"
          data-testid="skills-panel-group"
        >
          <Panel
            id="skills-list-panel"
            order={0}
            defaultSize={32}
            minSize={22}
            maxSize={45}
            className="min-h-0 min-w-0"
            data-testid="skills-list-panel"
          >
            <SkillsListPanel
              skills={skills}
              selectedSkillId={selectedSkillId}
              isLoading={skillsLoading}
              filter={filter}
              search={search}
              onFilterChange={setFilter}
              onSearchChange={setSearch}
              onCreateSkill={openCreateForm}
              onUploadSkill={openUploadPicker}
              onSelectSkill={requestSelectSkill}
            />
          </Panel>

          <PanelResizeHandle
            className="w-[1px] shrink-0 bg-zinc-200 transition-colors hover:bg-zinc-500"
            data-testid="skills-resize-handle"
          />

          <Panel
            id="skills-detail-panel"
            order={1}
            defaultSize={68}
            minSize={40}
            className="min-h-0 min-w-0"
            data-testid="skills-detail-panel"
          >
            <SkillDetailPanel
              error={error}
              onClearError={() => setError(null)}
              selection={{
                skillId: selectedSkillId,
                skill: selectedSkill,
                isLoading: detailLoading,
              }}
              form={{
                mode: formMode,
                values: formValues,
                initialValues: formInitialValues,
                isSaving:
                  createSkill.isPending ||
                  patchSkill.isPending ||
                  duplicateSkill.isPending,
                canCreateGlobal: isAdminUser(user),
                canCreateFormlyInternal: canCreateInternalSkill(user),
                onCancel: requestCloseForm,
                onSave: saveSkill,
                onValuesChange: setFormValues,
                onDraftWithAgent: draftFormWithAgent,
              }}
              savedSkillActions={{
                user,
                isSettingsSaving:
                  patchSkill.isPending || setSkillEnabled.isPending,
                onEdit: openEditForm,
                onDuplicate: openDuplicateForm,
                onDownload: downloadSkill,
                onRequestOpenSettings: requestOpenSkillSettings,
                onToggleEnabled: toggleSelectedSkillEnabled,
                onToggleForceEnabled: toggleSelectedSkillForceEnabled,
                onDelete: requestDeleteSelectedSkill,
              }}
            />
          </Panel>
        </PanelGroup>
      </Box>

      <input
        ref={uploadInputRef}
        type="file"
        accept=".md,text/markdown,text/plain"
        hidden
        onChange={(event) => handleUpload(event.target.files?.[0])}
      />

      <ConfirmationModal
        open={!!deleteTarget}
        title="Delete skill"
        content={
          deleteTarget
            ? `Delete "${deleteTarget.name}"? This will remove it for everyone who can access it.`
            : ""
        }
        confirmText="Delete"
        onAbort={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmationModal
        open={isDiscardConfirmOpen}
        title="Discard unsaved changes"
        content="Discard your unsaved skill changes?"
        confirmText="Discard"
        onAbort={abortDiscardChanges}
        onConfirm={confirmDiscardChanges}
      />
    </Box>
  );
}
